import { useCallback, useContext, useState, useEffect, useRef } from "react";
import "./tabstyles.css";
import "./dialog-styles.css";
import { AccountContext, ChallengeContext, WebSocketContext } from "../App.tsx";
import { ZoneContext } from "../views/Game.tsx";
import {
  Cross2Icon,
  InfoCircledIcon,
  QuestionMarkCircledIcon,
  QuestionMarkIcon,
} from "@radix-ui/react-icons";
import * as Popover from "@radix-ui/react-popover";
import * as Tabs from "@radix-ui/react-tabs";
import { Box } from "@radix-ui/themes";
import { EventBus } from "../game/EventBus";
import {
  EVENTS_NAME,
} from "../game/consts.ts";
import { acceptMission, completeObjective, guessAI, interactNpc } from "../api.ts";
import { getRichChallengeRenderer } from "./richChallenges.ts";
import { toast } from "./Toasts.tsx";
import { VIDEO_LOG_ITEMS, VIDEO_LOG_BASE_PATH } from "./videoLogs.ts";
import VideoLogPlayer from "./VideoLogPlayer.tsx";

// Hidden teaser finale: the /obd2 command jacks into mission 999 (the rogue
// vehicle) when the player is standing at the crashed car in the Roundabout.
const OBD2_MISSION_ID = 999;
const OBD2_GIVER_TILE_ID = 10999;

// Shown next to the title of any mission with more than one objective — the
// server only ever serves the player's current (first-incomplete) objective
// (ro-game-common player.rs), so clearing one and wanting the next means
// leaving the zone and walking back in. Native `title` gives a free hover
// tooltip with no new dependency.
function MultiObjectiveHint() {
  // A real tooltip rather than the native `title` attribute: `title` needs a
  // ~1s hover, never appears on touch at all, and is easily swallowed inside
  // the Radix popover — it silently did nothing here. This shows on hover,
  // on keyboard focus, AND on click/tap, so it works on a phone too.
  const [show, setShow] = useState(false);
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        verticalAlign: "middle",
        marginLeft: 6,
        color: "var(--ro-accent-bright)",
        cursor: "help",
      }}
      tabIndex={0}
      aria-label="What is a multi-part challenge?"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
      onClick={(e) => {
        // Tap-to-toggle for touch. stopPropagation so this never bubbles
        // into the popover's own open/close handling.
        e.stopPropagation();
        setShow((v) => !v);
      }}
    >
      <InfoCircledIcon width={16} height={16} />
      {show && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            // Anchored to the RIGHT edge and opening leftward: this dialog is
            // pinned to the right of the viewport, so a left-anchored tooltip
            // would run off-screen.
            right: 0,
            top: "calc(100% + 6px)",
            width: 240,
            padding: "8px 10px",
            background: "var(--ro-panel-solid, #0c1016)",
            border: "1px solid var(--ro-accent)",
            borderRadius: 4,
            color: "var(--ro-text)",
            fontSize: "0.78em",
            fontWeight: "normal",
            fontStyle: "normal",
            lineHeight: 1.45,
            letterSpacing: "normal",
            textTransform: "none",
            whiteSpace: "normal",
            zIndex: 5000,
            boxShadow: "0 4px 14px rgba(0,0,0,0.6)",
            pointerEvents: "none",
          }}
        >
          <strong style={{ color: "var(--ro-accent-bright)" }}>
            Multi-part challenge.
          </strong>{" "}
          This one has several parts. After you solve a part, leave the area
          and come back to get the next one.
        </span>
      )}
    </span>
  );
}

function ChallengeDialog() {
  const [challengeContext, setChallengeContext] = useContext(ChallengeContext);
  const [account] = useContext(AccountContext);
  // Field-notes: the challenge_response clues accumulate on the player and ride
  // to the client on PlayerInfo; surfaced here so traces (e.g. the U1984 pieces)
  // stay readable after a challenge is solved.
  const notes: string[] = (account as any)?.notes || [];
  // "Uncovered Video Log" rewards: player.items only carries {item_id,
  // item_count} — the actual title/clip mapping is the small hand-authored
  // VIDEO_LOG_ITEMS table (see videoLogs.ts), not anything the server sends.
  const unlockedVideos = ((account as any)?.items || [])
    .map((it: any) => VIDEO_LOG_ITEMS[it.item_id])
    .filter(Boolean) as { title: string; file: string }[];
  const [openVideo, setOpenVideo] = useState<{ title: string; file: string } | null>(null);
  const [zoneContext] = useContext(ZoneContext);
  const currentZone = (zoneContext?.currentZone as string) || "";
  const websocket = useContext(WebSocketContext);
  const [aka, setAka] = useState<string>("");
  const [query, setQuery] = useState<string>("");
  const [response, setResponse] = useState<string>("");
  const [laptopQuery, setLaptopQuery] = useState<string>("");
  const [laptopResponse, setLaptopResponse] = useState<string>("");

  // /guessai verdict — the server is the only thing that knows whether a name
  // is an AI (and it applies the reward/penalty), so the result has to come
  // back over the wire rather than being inferred here.
  useEffect(() => {
    const onGuess = (r: {
      name: string;
      correct: boolean;
      score: number;
      message: string;
    }) => {
      setLaptopResponse(
        `${r.correct ? ">>> MATCH" : ">>> NO MATCH"}\n${r.message}\nSCORE: ${r.score}`,
      );
    };
    EventBus.on(EVENTS_NAME.guessAiResult, onGuess);
    return () => {
      EventBus.removeListener(EVENTS_NAME.guessAiResult, onGuess);
    };
  }, []);
  // Controlled tab. Uncontrolled tabs "remember" the last tab used (e.g. the
  // Laptop tab after typing /obd2), so the next challenge would open stuck on
  // it. We snap back to the Challenges tab whenever a challenge opens/changes.
  const [activeTab, setActiveTab] = useState<string>("tab1");
  // Rich (full-screen) challenges only launch after an explicit "OPEN
  // INTERFACE" click — richOpenedFor tracks which challenge id was opened
  // that way. Defaulting to null means a challenge the player has NEVER
  // clicked into (including the very first encounter) is suppressed and
  // shows the plain popover instead — it used to default the other way
  // (tracking what was CLOSED, defaulting to "nothing closed yet" ==
  // unsuppressed), which made the very first ? click on a fresh challenge
  // auto-launch the full interface instead of showing the dialog.
  const [richOpenedFor, setRichOpenedFor] = useState<number | null>(null);
  const richAvailable = !!getRichChallengeRenderer(
    challengeContext.challengeType,
  );
  const richSuppressed =
    richAvailable && richOpenedFor !== challengeContext.challengeId;
  // Already solved? Server-computed (ro-game-common ClientMission.all_objectives_complete),
  // refreshed on every InteractNPCResponse. Multi-objective missions serve one
  // objective at a time (the player's first-incomplete), so a raw
  // objectives_complete>0 bitmask check would falsely read "won" after just
  // the FIRST of several objectives — this flag is the real signal.
  // Once won, the interface stays closed — no relaunch.
  const challengeWon = !!challengeContext.allObjectivesComplete;
  const prevDialogOpenRef = useRef<boolean | undefined>(
    challengeContext.challengeDialogOpen,
  );

  // Check if there's a puzzle available for the current challenge
  useEffect(() => {
    if (challengeContext && challengeContext.challengeGiver) {
      // Set the AKA based on the challenge giver
      switch (challengeContext.challengeGiver.toLowerCase()) {
        case "cee em dee":
          setAka("cmd");
          break;
        case "sky buns":
          setAka("buns");
          break;
        case "charles dexter ward":
          setAka("ward");
          break;
        case "kamasi":
          setAka("kam");
          break;
        case "joshua":
          setAka("josh");
          break;
        default:
          setAka("");
      }
    }
  }, [challengeContext]);

  // Effect to reset query and response when dialog closes
  useEffect(() => {
    // If dialog was open before and is now closed, reset the state
    if (
      prevDialogOpenRef.current === true &&
      challengeContext.challengeDialogOpen === false
    ) {
      setQuery("");
      setResponse("");
    }
    // Update the ref with current value
    prevDialogOpenRef.current = challengeContext.challengeDialogOpen;
  }, [challengeContext.challengeDialogOpen]);

  useEffect(() => {
    EventBus.on(EVENTS_NAME.wrongPhrase, () => {
      setResponse(`Incorrect answer. Boop beep boop 🤖. Try again!`);
    });

    return () => {
      EventBus.removeListener(EVENTS_NAME.wrongPhrase);
    };
  }, []);

  useEffect(() => {
    EventBus.on(EVENTS_NAME.correctPhrase, (message: string) => {
      setResponse(`Correct answer. ${message}`);
    });

    return () => {
      EventBus.removeListener(EVENTS_NAME.correctPhrase);
    };
  }, []);

  // Whenever a challenge opens (or we switch to a different one), force the
  // Challenges tab — never inherit the Laptop tab from the previous challenge.
  useEffect(() => {
    if (challengeContext.challengeDialogOpen) {
      setActiveTab("tab1");
    }
  }, [challengeContext.challengeDialogOpen, challengeContext.challengeId]);

  const testAnswer = useCallback(
    (answer: string) => {
      if (challengeContext.interactive) {
        setQuery(answer);
        setResponse("Looking up your answer... Please hold... 🎵🎶🎵🎶");
        if (websocket) {
          // check the answer
          completeObjective(
              websocket,
              challengeContext.challengeId!,
              challengeContext.objectiveId!,
              answer,
          );
        } else {
          setResponse(
              "Error: No websocket connection. Find an admin for help! 😱",
          );
        }
      }
    },
    [challengeContext.interactive, challengeContext.challengeId, challengeContext.objectiveId, websocket],
  );

  const engageLaptop = useCallback(() => {
    EventBus.emit(EVENTS_NAME.engageLaptop, {});
  }, []);

  // ESC dismisses an open rich challenge interface. Registered in the
  // CAPTURE phase so it fires even though the renderers' inputs
  // stopPropagation on their key handlers (capture runs top-down first).
  useEffect(() => {
    const richOpen =
      challengeContext.challengeDialogOpen && richAvailable && !richSuppressed;
    if (!richOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setResponse("");
      setRichOpenedFor(null);
      setChallengeContext({
        ...challengeContext,
        challengeDialogOpen: false,
      });
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [challengeContext, richAvailable, richSuppressed, setChallengeContext]);

  // Function to handle AI name guessing
  const guess = useCallback((name: string) => {
    console.log(name);
  }, []);

  return (
    <Box style={{ position: "fixed", right: "7px", top: "70px" }}>
      <Popover.Root
        open={challengeContext.challengeDialogOpen || false}
        onOpenChange={() => {
          setQuery("");
          setResponse("");
          setChallengeContext({
            ...challengeContext,
            challengeDialogOpen: !challengeContext.challengeDialogOpen,
          });
        }}
      >
        {/* Always clickable — this is the only way to reach the Laptop and
            Notes tabs, which have nothing to do with whether a challenge is
            nearby. challengeAvailable only changes the icon/label; it must
            never disable the trigger itself (that previously locked players
            out of their own laptop whenever they weren't standing in a
            challenge zone). */}
        <Popover.Trigger asChild>
          <button
            className={
              challengeContext.challengeAvailable ? "IconButton" : "IconButtonDisabled"
            }
            aria-label={
              challengeContext.challengeAvailable
                ? "Challenge available — click to open"
                : "Open laptop"
            }
          >
            {challengeContext.challengeAvailable ? (
              <QuestionMarkCircledIcon width={35} height={50} />
            ) : (
              <QuestionMarkIcon width={35} height={50} />
            )}
          </button>
        </Popover.Trigger>
        {challengeContext.challengeDialogOpen &&
          (!richAvailable || richSuppressed) && (
          <Popover.Portal>
            <Popover.Content className="PopoverContent" sideOffset={5}>
              <Tabs.Root
                className="TabsRoot"
                value={activeTab}
                onValueChange={setActiveTab}
              >
                <Tabs.List className="TabsList" aria-label="Challenge tabs">
                  <Tabs.Trigger className="TabsTrigger" value="tab1">
                    Challenges
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    className="TabsTrigger"
                    value="tab2"
                    onClick={() => {
                      engageLaptop();
                    }}
                  >
                    Laptop
                  </Tabs.Trigger>
                  <Tabs.Trigger className="TabsTrigger" value="tab3">
                    Notes{notes.length > 0 ? ` (${notes.length})` : ""}
                  </Tabs.Trigger>
                  <Tabs.Trigger className="TabsTrigger" value="tab4">
                    Logs{unlockedVideos.length > 0 ? ` (${unlockedVideos.length})` : ""}
                  </Tabs.Trigger>
                </Tabs.List>

                {/* Challenges Tab */}
                <Tabs.Content className="TabsContent" value="tab1">
                  {!challengeContext.challengeAvailable ? (
                    // Opened away from any challenge (e.g. just to reach the
                    // Laptop/Notes tabs) — challengeContext still holds
                    // whatever challenge was last active, so without this
                    // guard the tab would show stale title/text/buttons from
                    // wherever the player was standing before.
                    <Box
                      style={{
                        margin: "20px 10px",
                        fontStyle: "italic",
                        color: "var(--ro-text)",
                        opacity: 0.7,
                      }}
                    >
                      &lt;no challenge selected&gt;
                    </Box>
                  ) : richAvailable ? (
                    // Rich challenge: its content lives in the dedicated
                    // full-screen interface — don't duplicate it here.
                    <div
                      style={{
                        marginTop: 10,
                        padding: "5px",
                        color: "var(--ro-text)",
                      }}
                    >
                      <Box style={{ fontWeight: "bold", margin: "10px" }}>
                        {challengeContext.challengeTitle}
                        {(challengeContext.totalObjectives || 1) > 1 && (
                          <MultiObjectiveHint />
                        )}
                      </Box>
                      <Box
                        style={{
                          margin: "10px",
                          fontStyle: "italic",
                          color: "var(--ro-accent-bright)",
                        }}
                      >
                        {challengeWon
                          ? "Signal recovered. This system answers to you now."
                          : "This challenge runs on a dedicated interface."}
                      </Box>
                      <Box style={{ margin: "10px" }}>
                        {challengeWon ? (
                          <span
                            style={{
                              color: "var(--ro-accent-bright)",
                              letterSpacing: "0.12em",
                            }}
                          >
                            ✓ COMPLETE
                          </span>
                        ) : (
                          <button
                            className="Button green"
                            onClick={() =>
                              setRichOpenedFor(challengeContext.challengeId ?? null)
                            }
                          >
                            OPEN INTERFACE
                          </button>
                        )}
                      </Box>
                    </div>
                  ) : (
                  <>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        marginBottom: 10,
                        marginTop: 10,
                        height: "300px",
                        padding: "5px",
                        border: "1px solid grey",
                        overflowY: "scroll",
                        color: "var(--ro-text)" /* Gold highlight color */,
                      }}
                    >
                      <Box
                        style={{
                          fontWeight: "bold",
                          margin: "10px",
                        }}
                      >
                        {challengeContext.challengeTitle}
                        {(challengeContext.totalObjectives || 1) > 1 && (
                          <MultiObjectiveHint />
                        )}
                      </Box>
                      <Box
                        style={{
                          margin: "10px",
                          fontStyle: "italic",
                          color: "var(--ro-accent-bright)" /* Gold highlight color */,
                        }}
                      >
                        {!challengeContext.interactive && (
                          <span>
                            ~ {challengeContext.challengeGiver}{" "}
                            {aka && <span>AKA {aka}</span>} ~
                          </span>
                        )}
                      </Box>
                      <Box style={{ margin: "10px", whiteSpace: "pre-wrap" }}>
                        {challengeContext.challengeText}
                      </Box>
                    </div>
                  </div>
                  {challengeWon ? (
                    // The giver keeps offering this mission forever --
                    // get_missions_by_giver_tile_id_for_player has no
                    // "already fully complete" filter, by design (it also
                    // has to keep serving an IN-PROGRESS multi-objective
                    // mission). The rich-challenge branch above already
                    // checks challengeWon before showing its input; this
                    // plain branch never did, so re-entering a finished
                    // mission's zone re-opened a live answer box on
                    // objective content that was never going to accept
                    // another answer -- indistinguishable from "stuck" to a
                    // player, worse on a multi-objective mission whose
                    // objectives happen to share near-identical wording.
                    <Box style={{ margin: "10px" }}>
                      <span
                        style={{
                          color: "var(--ro-accent-bright)",
                          letterSpacing: "0.12em",
                        }}
                      >
                        ✓ COMPLETE
                      </span>
                    </Box>
                  ) : (
                  <>
                  <Box style={{ height: "40px" }}>
                    <input
                      className="Input"
                      id="answer"
                      style={{ width: "68%" }}
                      tabIndex={-1}
                      onKeyDownCapture={(event) => {
                        event?.stopPropagation();
                        if (event.key === "Enter") {
                          let targetElement =
                            document.getElementById("askButton");
                          if (targetElement) {
                            targetElement.click();
                          }
                        }
                      }}
                    />
                    &nbsp;&nbsp;
                    <button
                      id="askButton"
                      className="Button green"
                      onClick={() => {
                        const answer = (
                          document.getElementById("answer") as HTMLInputElement
                        ).value;
                        if (answer) {
                          testAnswer(answer);
                          (
                            document.getElementById(
                              "answer",
                            ) as HTMLInputElement
                          ).value = "";
                        }
                      }}
                    >
                      {challengeContext.interactive ? "Send" : "Ask"}
                    </button>
                  </Box>
                  <Box
                    style={{
                      color: "#e3c6b4",
                      fontSize: ".95em",
                      fontStyle: "italic",
                      display: "block",
                    }}
                  >
                    {query && <div> &lt;&lt; {query}</div>}
                    {response && <div> &gt;&gt; {response}</div>}
                  </Box>
                  </>
                  )}
                  </>
                  )}
                </Tabs.Content>

                {/* Laptop Tab */}
                <Tabs.Content className="TabsContent" value="tab2">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        marginBottom: 10,
                        marginTop: 10,
                        height: "300px",
                        padding: "5px",
                        border: "1px solid grey",
                        overflowY: "scroll",
                        color: "var(--ro-text)" /* Gold highlight color */,
                      }}
                    >
                      <Box style={{ margin: "10px", whiteSpace: "pre-wrap" }}>
                        {laptopResponse ||
                          "Welcome to your laptop terminal. Type a command to begin."}
                      </Box>
                    </div>
                  </div>
                  <Box style={{ height: "40px" }}>
                    <input
                      className="Input"
                      id="laptopCommand"
                      style={{ width: "70%" }}
                      tabIndex={-1}
                      onKeyDownCapture={(event) => {
                        event?.stopPropagation();
                        if (event.key === "Enter") {
                          let targetElement =
                            document.getElementById("laptopButton");
                          if (targetElement) {
                            targetElement.click();
                          }
                        }
                      }}
                    />
                    &nbsp;&nbsp;
                    <button
                      id="laptopButton"
                      className="Button green compact"
                      onClick={() => {
                        const command = (
                          document.getElementById(
                            "laptopCommand",
                          ) as HTMLInputElement
                        ).value;
                        if (command) {
                          setLaptopQuery(command);

                          // Process commands
                          if (command === "/help") {
                            setLaptopResponse(
                              "Available commands:\n/help - Show this help message\n/guessai <name> - Guess AI by name",
                            );
                          } else if (command.startsWith("/guessai ")) {
                            if (websocket) {
                              const name = command.substring(9).trim();
                              guess(name);
                              setLaptopResponse(
                                `Attempting to guess AI: ${name}`,
                              );
                              guessAI(websocket, name);
                            } else {
                              toast("Error", "No websocket connection.", "error");
                            }
                          } else if (
                            ["/obd2", "/odb2", "/obdii", "/obd", "/odb"].includes(
                              command.trim().toLowerCase(),
                            )
                          ) {
                            // Hidden command: only works standing at the dead
                            // car in the Roundabout. Jacks into its diagnostics.
                            // Forgiving on the easy obd<->odb transposition and
                            // case, since players guess this from a spoken hint.
                            if (!currentZone.toLowerCase().includes("roundabout")) {
                              setLaptopResponse(
                                "/obd2: NO VEHICLE IN RANGE.\nFind a dead car and try again.",
                              );
                            } else if (websocket) {
                              interactNpc(websocket, OBD2_GIVER_TILE_ID);
                              acceptMission(
                                websocket,
                                OBD2_GIVER_TILE_ID,
                                OBD2_MISSION_ID,
                              );
                              setResponse("");
                              setLaptopResponse(
                                "/obd2: handshake accepted. Jacking into the on-board diagnostics…",
                              );
                            } else {
                              toast("Error", "No websocket connection.", "error");
                            }
                          } else {
                            setLaptopResponse(
                              "Command processed: " +
                                command +
                                "\n[no such command]\nType /help for available commands",
                            );
                          }

                          (
                            document.getElementById(
                              "laptopCommand",
                            ) as HTMLInputElement
                          ).value = "";
                        }
                      }}
                    >
                      Execute
                    </button>
                  </Box>
                  <Box
                    style={{
                      color: "#e3c6b4",
                      fontSize: ".95em",
                      fontStyle: "italic",
                      display: "block",
                    }}
                  >
                    {laptopQuery && <div> &lt;&lt; {laptopQuery}</div>}
                  </Box>
                </Tabs.Content>

                {/* Notes Tab — accumulated trace clues, always available */}
                <Tabs.Content className="TabsContent" value="tab3">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        marginBottom: 10,
                        marginTop: 10,
                        height: "340px",
                        padding: "5px",
                        border: "1px solid grey",
                        overflowY: "scroll",
                        color: "var(--ro-text)",
                      }}
                    >
                      <Box
                        style={{
                          fontWeight: "bold",
                          margin: "10px",
                        }}
                      >
                        Field Notes
                      </Box>
                      {notes.length === 0 ? (
                        <Box
                          style={{
                            margin: "10px",
                            fontStyle: "italic",
                            color: "var(--ro-accent-bright)",
                          }}
                        >
                          No traces recovered yet. Solve challenges to collect
                          fragments here.
                        </Box>
                      ) : (
                        notes.map((note, i) => (
                          <Box
                            key={i}
                            style={{
                              margin: "10px",
                              padding: "8px",
                              borderLeft: "2px solid var(--ro-accent)",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {note}
                          </Box>
                        ))
                      )}
                    </div>
                  </div>
                </Tabs.Content>

                {/* Video Logs Tab — "Uncovered Video Log" reward items.
                    Each thumbnail is the clip itself, paused at its first
                    frame (preload="metadata", no explicit poster asset
                    needed) — clicking opens the full watch overlay. */}
                <Tabs.Content className="TabsContent" value="tab4">
                  <div
                    style={{
                      marginBottom: 10,
                      marginTop: 10,
                      height: "340px",
                      padding: "5px",
                      border: "1px solid grey",
                      overflowY: "scroll",
                      color: "var(--ro-text)",
                    }}
                  >
                    <Box style={{ fontWeight: "bold", margin: "10px" }}>
                      Uncovered Video Logs
                    </Box>
                    {unlockedVideos.length === 0 ? (
                      <Box
                        style={{
                          margin: "10px",
                          fontStyle: "italic",
                          color: "var(--ro-accent-bright)",
                        }}
                      >
                        No footage recovered yet. Some challenge rewards carry
                        a log — solve enough and one turns up.
                      </Box>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 10,
                          margin: "10px",
                        }}
                      >
                        {unlockedVideos.map((v, i) => (
                          <button
                            key={i}
                            onClick={() => setOpenVideo(v)}
                            aria-label={`Watch ${v.title}`}
                            style={{
                              width: 120,
                              padding: 4,
                              background: "transparent",
                              border: "1px solid var(--ro-accent)",
                              borderRadius: 4,
                              cursor: "pointer",
                              color: "var(--ro-text)",
                            }}
                          >
                            <video
                              src={`${VIDEO_LOG_BASE_PATH}${v.file}`}
                              preload="metadata"
                              muted
                              tabIndex={-1}
                              style={{
                                width: "100%",
                                height: 68,
                                objectFit: "cover",
                                display: "block",
                                background: "black",
                              }}
                            />
                            <div
                              style={{
                                fontSize: ".75em",
                                marginTop: 4,
                                letterSpacing: "0.04em",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {v.title}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Tabs.Content>
              </Tabs.Root>

              <Popover.Close className="PopoverClose" aria-label="Close">
                <Cross2Icon />
              </Popover.Close>
              <Popover.Arrow className="PopoverArrow" />
            </Popover.Content>
          </Popover.Portal>
        )}
      </Popover.Root>

      {/* Rich-challenge renderer (challenge_type driven). The matching
          component, if any, takes over the screen; the plain popover above is
          suppressed for the same type. Same submit/validation flow as text.
          Also gated on challengeAvailable: if a player walked away while this
          was open (never explicitly closing it, so richOpenedFor is still
          set), it must not resurface full-screen away from any challenge. */}
      {challengeContext.challengeDialogOpen &&
        challengeContext.challengeAvailable &&
        !richSuppressed &&
        (() => {
          const Renderer = getRichChallengeRenderer(
            challengeContext.challengeType,
          );
          if (!Renderer) return null;
          return (
            <Renderer
              payload={challengeContext.challengePayload || ""}
              response={response}
              title={challengeContext.challengeTitle}
              text={challengeContext.challengeText}
              onSubmit={(c) => testAnswer(c)}
              onClose={() => {
                // Keep challengeType so the ? button / Challenges tab can
                // relaunch the interface without re-entering the zone; just
                // clear the "explicitly opened" mark so it needs another
                // OPEN INTERFACE click rather than auto-relaunching.
                setResponse("");
                setRichOpenedFor(null);
                setChallengeContext({
                  ...challengeContext,
                  challengeDialogOpen: false,
                });
              }}
            />
          );
        })()}

      {openVideo && (
        <VideoLogPlayer
          title={openVideo.title}
          file={openVideo.file}
          onClose={() => setOpenVideo(null)}
        />
      )}
    </Box>
  );
}

export default ChallengeDialog;
